import * as React from 'react';

const STORAGE_KEY = 'app-font-scale';
const BASE_PX = 16;
const SCALES = [1, 1.125, 1.25, 1.5];
const DEFAULT_SCALE = 1;

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    const n = v != null ? parseFloat(v, 10) : NaN;
    return Number.isFinite(n) && SCALES.includes(n) ? n : DEFAULT_SCALE;
  } catch {
    return DEFAULT_SCALE;
  }
}

export const FontScaleContext = React.createContext({
  scale: DEFAULT_SCALE,
  setScale: () => {},
  cycleScale: () => {},
});

export function FontScaleProvider({ children }) {
  const [scale, setScaleState] = React.useState(DEFAULT_SCALE);

  React.useEffect(() => {
    setScaleState(readStored());
  }, []);

  React.useEffect(() => {
    document.documentElement.style.fontSize = `${BASE_PX * scale}px`;
    try {
      localStorage.setItem(STORAGE_KEY, String(scale));
    } catch {}
  }, [scale]);

  const cycleScale = React.useCallback(() => {
    setScaleState((s) => {
      const i = SCALES.indexOf(s);
      const next = i < 0 || i >= SCALES.length - 1 ? SCALES[0] : SCALES[i + 1];
      return next;
    });
  }, []);

  const setScale = React.useCallback((next) => {
    setScaleState((s) => (typeof next === 'function' ? next(s) : next));
  }, []);

  const value = React.useMemo(
    () => ({ scale, setScale, cycleScale }),
    [scale, setScale, cycleScale],
  );

  return (
    <FontScaleContext.Provider value={value}>
      {children}
    </FontScaleContext.Provider>
  );
}

export function useFontScale() {
  const ctx = React.useContext(FontScaleContext);
  if (ctx == null) {
    return {
      scale: DEFAULT_SCALE,
      setScale: () => {},
      cycleScale: () => {},
    };
  }
  return ctx;
}
